import React, { useState } from "react";
import {
  Phone,
  Wallet,
  Shield,
  ChevronRight,
  Eye,
  EyeOff,
  X,
  Copy,
  CreditCard,
  PieChart,
} from "lucide-react";
import { formatMoney } from "../utils/money";

const Profile = ({ isOpen, onClose, user, walletBalances, onLogout }) => {
  const [showPinChange, setShowPinChange] = useState(false);
  const [showBalances, setShowBalances] = useState(false);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    // You could add a toast notification here
  };

  const menuSections = [
    {
      title: "Account",
      items: [
        // {
        //   icon: User,
        //   label: "Personal Information",
        //   description: "Update your details",
        //   action: () => console.log("Personal info"),
        // },
        {
          icon: Phone,
          label: "Phone Number",
          description: user?.phone || "Not set",
          action: () => console.log("Phone"),
        },
        {
          icon: Shield,
          label: "Change PIN",
          description: "Update security PIN",
          action: () => setShowPinChange(true),
        },
      ],
    },
  ];

  const PinChangeModal = () => (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="w-full max-w-sm bg-white rounded-3xl p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">Change PIN</h2>
          <button
            onClick={() => setShowPinChange(false)}
            className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Current PIN
            </label>
            <input
              type="password"
              maxLength="6"
              className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-center text-lg tracking-widest"
              placeholder="••••••"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              New PIN
            </label>
            <input
              type="password"
              maxLength="6"
              className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-center text-lg tracking-widest"
              placeholder="••••••"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Confirm New PIN
            </label>
            <input
              type="password"
              maxLength="6"
              className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-center text-lg tracking-widest"
              placeholder="••••••"
            />
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              onClick={() => setShowPinChange(false)}
              className="flex-1 py-3 px-4 border border-gray-200 rounded-xl text-gray-700 font-medium"
            >
              Cancel
            </button>
            <button className="flex-1 py-3 px-4 bg-yellow-400 rounded-xl text-black font-medium">
              Update PIN
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-yellow-400 to-yellow-500 px-4 pt-12 pb-8">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full bg-white bg-opacity-80 flex items-center justify-center"
          >
            <X className="w-5 h-5 text-black" />
          </button>
          <h1 className="text-lg font-bold text-black">Profile</h1>
          <div className="w-10" />
        </div>

        {/* User Info */}
        <div className="flex items-center space-x-4 pb-3">
          <div className="w-16 h-16 rounded-full bg-white bg-opacity-30 p-1 flex items-center justify-center ">
            <img
              src="/avatar.png"
              alt="Profile Avatar"
              className="w-full h-full rounded-full object-cover"
              onError={(e) => {
                // Fallback to initials if image fails to load
                e.target.style.display = "none";
                e.target.nextSibling.style.display = "flex";
              }}
            />
            <div className="w-full h-full rounded-full bg-white bg-opacity-20  items-center justify-center hidden">
              <span className="text-black font-bold text-xl">
                {user?.first_name?.charAt(0).toUpperCase()}
                {user?.last_name?.charAt(0).toUpperCase() || ""}
              </span>
            </div>
          </div>
          <div className="flex-1 pb-2">
            <h2 className="text-xl font-light text-black">
              <b className="font-extrabold">{user?.first_name}</b>{" "}
              {user?.last_name}
            </h2>
            <div className="flex items-center space-x-2 mt-1 ">
              <p className="text-black text-opacity-80 text-sm">
                {user?.phone}
              </p>
              <button
                onClick={() => copyToClipboard(user?.phone)}
                className="p-1 rounded-full bg-white bg-opacity-20"
              >
                <Copy className="w-3 h-3 text-black" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Wallet Overview */}
      <div className="px-4 -mt-6 mb-6">
        <div className="bg-white rounded-2xl rounded-t-[40px] p-4 border border-gray-100 shadow-sm">
          <div className="flex items-center justify-between mb-4 px-0">
            <h3 className=" font-semibold text-gray-900">Wallet Balance</h3>
            <button
              onClick={() => setShowBalances(!showBalances)}
              className="p-2 rounded-full bg-gray-100"
            >
              {showBalances ? (
                <EyeOff className="w-4 h-4 text-gray-600" />
              ) : (
                <Eye className="w-4 h-4 text-gray-600" />
              )}
            </button>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="text-center">
              <div className="flex items-center justify-center w-12 h-12 rounded-full bg-blue-100 mx-auto mb-2">
                <CreditCard className="w-6 h-6 text-blue-600" />
              </div>
              <p className="text-xs text-gray-500 mb-1">Affordable Data</p>
              <p className="text-lg font-bold text-gray-900">
                {showBalances
                  ? `₵${formatMoney(walletBalances?.affordable_data_balance)}`
                  : "••••"}
              </p>
            </div>

            <div className="text-center">
              <div className="flex items-center justify-center w-12 h-12 rounded-full bg-purple-100 mx-auto mb-2">
                <Wallet className="w-6 h-6 text-purple-600" />
              </div>
              <p className="text-xs text-gray-500 mb-1">Airtime & Data</p>
              <p className="text-lg font-bold text-gray-900">
                {showBalances
                  ? `₵${formatMoney(walletBalances?.airtime_data_balance)}`
                  : "••••"}
              </p>
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <PieChart className="w-4 h-4 text-emerald-600" />
                <span className="text-sm text-gray-600">Commission Rate</span>
              </div>
              <span className="text-sm font-semibold text-emerald-600">
                {formatMoney(walletBalances?.commission_rate)}%
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Menu Sections */}
      <div className="px-4 pb-8 overflow-y-auto max-h-96">
        {menuSections.map((section, sectionIndex) => (
          <div key={sectionIndex} className="mb-6">
            <h3 className=" font-semibold text-gray-900 mb-2 px-2">
              {section.title}
            </h3>
            <div className="bg-white rounded-2xl border border-gray-100">
              {section.items.map((item, itemIndex) => (
                <button
                  key={itemIndex}
                  onClick={item.action}
                  className="w-full flex items-center space-x-3 p-4 hover:bg-gray-50 transition-colors first:rounded-t-2xl last:rounded-b-2xl"
                >
                  <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-5 h-5 text-gray-600" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-medium text-gray-900">{item.label}</p>
                    <p className="text-sm text-gray-500">{item.description}</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {showPinChange && <PinChangeModal />}
    </div>
  );
};

export default Profile;
